import {
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faEnvelope,
  faReply,
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { dateFormatter } from "helpers/utils";
import { useAtom } from "jotai";
import { paginationAtom } from "jotai/DealJotaiInstance";
import { Pagination, Card, Badge, Form, Button, Collapse } from "react-bootstrap";
import { useState } from "react";
import { 
  UilPhone, 
  UilCalendarAlt, 
  UilEnvelope,
  UilCheckCircle,
  UilExclamationTriangle,
  UilUser,
  UilClipboard
} from "@iconscout/react-unicons";
import EmailDetailModal from './EmailDetailModal';

const DealDetailsTimeline = ({ activities, allActivities }: any) => {
  const [pagination, setPagination] = useAtom(paginationAtom);
  const { limit, offset, total } = pagination;
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [expandedEmailId, setExpandedEmailId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState<string>('');

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const maxVisiblePages = 4;

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({
      ...prev,
      offset: pageNumber - 1,
      // offset: (pageNumber - 1) * limit,
    }));
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === offset + 1}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return pages;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call_note': return UilPhone;
      case 'email': return UilEnvelope;
      case 'meeting': return UilCalendarAlt;
      case 'task': return UilCheckCircle;
      case 'note': return UilClipboard;
      default: return UilUser;
    }
  };

  const getActivityType = (type: string) => {
    switch (type) {
      case 'call_note': return { type: 'Call', color: 'warning' };
      case 'email': return { type: 'Email', color: 'danger' };
      case 'meeting': return { type: 'Meeting', color: 'info' };
      case 'task': return { type: 'Task', color: 'success' };
      case 'note': return { type: 'Note', color: 'primary' };
      default: return { type: 'Activity', color: 'secondary' };
    }
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPriorityLevel = (index: number) => {
    if (index === 0) return 'high';
    if (index < 3) return 'medium';
    return 'normal';
  };

  const getActivityTitle = (item: any) => {
    if (item.type === 'email') {
      return item.subject || '(No Subject)';
    }
    
    const title = item.note || item.call_note || item.meeting_name || item.task || 'Activity';
    return title;
  };

  const getTaskCompletionIndicator = (item: any) => {
    // Completion indicator removed - now shown in header only
    return null;
  };

  const handleActivityClick = (item: any) => {
    console.log('Activity clicked:', item); // Debug log
    // Skip opening the modal if we're currently in quick reply mode for this item
    if (expandedEmailId === item.id) {
      return;
    }
    
    if (item.type === 'email') {
      // Debug: Check what email fields are available
      console.log('📧 [FRONTEND] Email item clicked:', item);
      console.log('📧 [FRONTEND] Available email fields:', Object.keys(item));
      console.log('📧 [FRONTEND] Body content check:', {
        body: item.body,
        email_body: item.email_body, 
        snippet: item.snippet,
        body_html: item.body_html,
        body_text: item.body_text
      });
      
      // Convert the deal activity data to ActivityTimelineData format for the modal
      const activityData = {
        id: item.id,
        oppositeContent: {
          date: item.created_on || item.email_creation_date,
          time: new Date(item.created_on || item.email_creation_date).toLocaleTimeString()
        },
        content: {
          title: `Email: ${item.subject || '(No Subject)'}`,
          user: 'System', // Or extract from your actual data
          details: `Email ${item.direction === 'sent' ? 'sent to' : 'received from'} ${item.direction === 'sent' ? item.to : item.from}`,
          email: {
            subject: item.subject || '(No Subject)',
            from: item.from || item.sender_email || 'Unknown',
            to: item.to || item.recipient_email || 'Unknown',
            body: item.body || item.email_body || item.snippet || item.body_html || item.body_text || 'No content available',
            cc: item.cc || undefined
          }
        },
        separator: {
          icon: faEnvelope
        }
      };
      console.log('Setting modal data:', activityData); // Debug log
      setSelectedActivity(activityData);
      setShowModal(true);
      console.log('Modal should show now. showModal state:', true); // Debug log
    }
  };

  const handleCloseModal = () => {
    console.log('Closing modal');
    setShowModal(false);
    setSelectedActivity(null);
  };

  // Debug logging for modal state
  console.log('DealDetailsTimeline render - Modal state:', { showModal, selectedActivity });

  const isClickable = (item: any) => {
    return item.type === 'email' && (
      (item.body && item.body.trim() !== '') || 
      item.subject || 
      item.from || 
      item.to
    );
  };

  const handleQuickReply = (item: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the modal
    e.preventDefault(); // Ensure no default behavior occurs
    setExpandedEmailId(expandedEmailId === item.id ? null : item.id);
    setReplyText('');
  };

  const handleSendReply = async (item: any) => {
    if (!replyText.trim()) return;
    
    // Here you would integrate with your email sending API
    console.log('Sending reply:', {
      to: item.from || item.sender_email,
      subject: `Re: ${item.subject || '(No Subject)'}`,
      body: replyText,
      inReplyTo: item.id
    });
    
    // Reset state
    setReplyText('');
    setExpandedEmailId(null);
    
    // You could show a success toast here
    // toast.success('Reply sent successfully!');
  };

  const handleCancelReply = () => {
    setExpandedEmailId(null);
    setReplyText('');
  };

  return (
    <div className="modern-activity-timeline mt-2">
      {/* Recent Activity Header */}
      <div className="activity-header mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h6 className="mb-1 fw-semibold">Recent Deal Activity</h6>
            <small className="text-muted">Stay on top of your deal engagement</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="fs-10">
              {allActivities.length} Activities
            </Badge>
          </div>
        </div>
      </div>

      {/* Modern Activity Cards */}
      <div className="activity-feed">
        {allActivities.map((item: any, index: any) => {
          const ActivityIcon = getActivityIcon(item.type);
          const activityType = getActivityType(item.type);
          const priority = getPriorityLevel(index);
          const activityTitle = getActivityTitle(item);
          const clickable = isClickable(item);
          
          return (
            <Card 
              key={item.id} 
              className={classNames(
                'activity-card mb-3 border-0',
                {
                  'recent-activity': index === 0,
                  'high-priority': priority === 'high',
                  'medium-priority': priority === 'medium',
                  'clickable-card': clickable
                }
              )}
              style={{ 
                cursor: clickable ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ...(clickable && {
                  transform: 'scale(1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                })
              }}
              onMouseEnter={(e) => {
                if (clickable) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (clickable) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }
              }}
              onClick={() => clickable && handleActivityClick(item)}
            >
              <Card.Body className="p-3">
                <div className="d-flex align-items-start">
                  {/* Activity Icon */}
                  <div className={classNames(
                    'activity-icon me-3 flex-shrink-0',
                    `icon-${activityType.color}`
                  )}>
                    <ActivityIcon size="20" />
                  </div>

                  {/* Activity Content */}
                  <div className="activity-content flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <Badge 
                          bg={activityType.color} 
                          className="activity-type-badge"
                        >
                          {activityType.type}
                        </Badge>
                        {index === 0 && (
                          <Badge bg="danger" className="fs-11">
                            Latest
                          </Badge>
                        )}
                        {priority === 'high' && (
                          <UilExclamationTriangle size="14" className="text-warning" />
                        )}

                      </div>
                      <small className="text-muted activity-time">
                        {getTimeAgo(item.created_on || item.email_creation_date)}
                      </small>
                    </div>

                    <h6 className={classNames(
                      'activity-title mb-2 fs-8',
                      {
                        'text-decoration-line-through text-muted': item.type === 'task' && (item.is_completed || item.status === 'Completed')
                      }
                    )}>
                      {activityTitle}
                      {getTaskCompletionIndicator(item)}
                    </h6>

                    {/* Email-specific content */}
                    {item.type === 'email' && (
                      <div className="email-activity-details mb-2">
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <small className="text-muted">
                            <strong>{item.direction === 'sent' ? 'To:' : 'From:'}</strong> {item.direction === 'sent' ? item.to : item.from}
                          </small>
                          {/* Quick Actions */}
                          <div className="email-quick-actions d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="btn-sm px-2 py-1"
                              onClick={(e) => handleQuickReply(item, e)}
                              title="Quick Reply"
                            >
                              <FontAwesomeIcon icon={faReply} size="xs" />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="btn-sm px-2 py-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('View Full Email button clicked for item:', item);
                                // Only open the modal if we're not in quick reply mode
                                if (expandedEmailId !== item.id) {
                                  handleActivityClick(item);
                                }
                              }}
                              title="View Full Email"
                            >
                              <FontAwesomeIcon icon={faEnvelope} size="xs" />
                            </Button>
                          </div>
                        </div>
                        {item.body && item.body.trim() !== '' ? (
                          <p className="email-preview mb-0 text-muted" style={{ 
                            fontSize: '0.8rem',
                            lineHeight: '1.3',
                            maxHeight: '2.6rem',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical' as const
                          }}>
                            {(() => {
                              // Enhanced email preview formatting
                              let preview = item.body.replace(/<[^>]+>/g, '').trim();
                              
                              // Remove common email artifacts for preview
                              preview = preview
                                .replace(/📧 Forwarded Email[\s\S]*?$/, '')
                                .replace(/Original Subject:[\s\S]*?$/, '')
                                .replace(/Forwarded by:[\s\S]*?$/, '')
                                .replace(/From:[\s\S]*?Sent:[\s\S]*?To:[\s\S]*?Subject:[\s\S]*?$/, '')
                                .replace(/_{10,}/g, '')
                                .replace(/Please consider the environment[\s\S]*?$/, '')
                                .replace(/This electronic mail message[\s\S]*?$/, '')
                                .replace(/Thanks,[\s\S]*$/, '')
                                .replace(/Best regards,[\s\S]*$/, '')
                                .replace(/Sincerely,[\s\S]*$/, '')
                                .replace(/Best,[\s\S]*$/, '')
                                .replace(/\s{3,}/g, ' ')
                                .trim();
                              
                              return preview.length > 200 
                                ? `${preview.substring(0, 200)}...` 
                                : preview || 'No content available';
                            })()}
                          </p>
                        ) : (
                          <p className="email-preview mb-0" style={{ 
                            fontSize: '0.8rem',
                            lineHeight: '1.3',
                            color: '#dc3545',
                            fontStyle: 'italic',
                          }}>
                            📧 Email content not available - click to view details
                          </p>
                        )}
                        
                        {/* Quick Reply Form */}
                        <Collapse in={expandedEmailId === item.id}>
                          <div 
                            className="quick-reply-form mt-3 p-3 bg-light rounded" 
                            onClick={(e) => e.stopPropagation()} 
                            onFocus={(e) => e.stopPropagation()}
                          >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <small className="fw-semibold text-primary">
                                <FontAwesomeIcon icon={faReply} className="me-1" />
                                Quick Reply to {item.from || item.sender_email}
                              </small>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={handleCancelReply}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Type your reply here..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent modal opening on click
                              onFocus={(e) => e.stopPropagation()} // Prevent modal opening on focus
                              className="mb-2"
                            />
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleCancelReply();
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSendReply(item);
                                }}
                                disabled={!replyText.trim()}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    )}

                    <div className="activity-footer d-flex align-items-center justify-content-between">
                      <div className="activity-user d-flex align-items-center">
                        <div className="user-avatar me-2">
                          <div className="avatar-circle bg-secondary">
                            <UilUser size="12" className="text-white" />
                          </div>
                        </div>
                        <small className="text-muted">
                          <FontAwesomeIcon
                            icon={faCalendarDays}
                            className="text-primary me-1"
                            size="sm"
                          />
                          {dateFormatter(item.created_on || item.email_creation_date)}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {/* Modern Pagination */}
      {allActivities.length > 0 && (
        <div className="modern-pagination mt-4 d-flex justify-content-center">
          <Pagination className="modern-pagination-controls">
            <Pagination.Prev
              disabled={offset + 1 === 1}
              onClick={handlePrevious}
              className="pagination-control"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Pagination.Prev>
            {getPageNumbers()}
            <Pagination.Next
              disabled={offset + 1 === totalPages}
              onClick={handleNext}
              className="pagination-control"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Pagination.Next>
          </Pagination>
        </div>
      )}

      {/* Email Detail Modal */}
      <EmailDetailModal 
        show={showModal}
        onHide={handleCloseModal}
        activityData={selectedActivity}
      />

      {/* Modern Timeline Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-activity-timeline {
          --timeline-primary: #0d6efd;
          --timeline-success: #198754;
          --timeline-info: #0dcaf0;
          --timeline-warning: #ffc107;
          --timeline-danger: #dc3545;
          --timeline-secondary: #6c757d;
          --timeline-light: #f8f9fa;
          --timeline-dark: #212529;
          --timeline-border: #e9ecef;
          --timeline-shadow: 0 2px 8px rgba(0,0,0,0.08);
          --timeline-shadow-hover: 0 4px 16px rgba(0,0,0,0.12);
        }

        .activity-header {
          border-bottom: 1px solid var(--timeline-border);
          padding-bottom: 1rem;
        }

        .activity-feed {
          margin-top: 1rem;
        }

        .activity-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 12px !important;
          box-shadow: var(--timeline-shadow);
          transition: all 0.3s ease;
          overflow: hidden;
        }

        .activity-card:hover {
          box-shadow: var(--timeline-shadow-hover);
        }

        .activity-card.recent-activity {
          border-left: 4px solid var(--timeline-primary);
        }

        .activity-card.high-priority {
          border-left: 4px solid var(--timeline-danger);
        }

        .activity-card.medium-priority {
          border-left: 4px solid var(--timeline-warning);
        }

        .activity-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .activity-icon.icon-primary {
          background: linear-gradient(135deg, var(--timeline-primary), #0056b3);
        }

        .activity-icon.icon-success {
          background: linear-gradient(135deg, var(--timeline-success), #0d5224);
        }

        .activity-icon.icon-info {
          background: linear-gradient(135deg, var(--timeline-info), #055160);
        }

        .activity-icon.icon-warning {
          background: linear-gradient(135deg, var(--timeline-warning), #664d03);
        }

        .activity-icon.icon-danger {
          background: linear-gradient(135deg, var(--timeline-danger), #721c24);
        }

        .activity-icon.icon-secondary {
          background: linear-gradient(135deg, var(--timeline-secondary), #495057);
        }

        .activity-type-badge {
          font-size: 0.7rem !important;
          font-weight: 600 !important;
          padding: 0.3rem 0.6rem !important;
          border-radius: 8px !important;
        }

        .activity-title {
          color: var(--timeline-dark);
          font-weight: 600;
          line-height: 1.3;
        }

        .activity-time {
          font-size: 0.75rem;
          color: var(--timeline-secondary);
        }

        .email-activity-details {
          background: rgba(248, 249, 250, 0.5);
          border-radius: 8px;
          padding: 0.75rem;
          margin-top: 0.5rem;
        }

        .email-quick-actions .btn {
          border-radius: 6px !important;
          font-size: 0.7rem !important;
          transition: all 0.2s ease !important;
        }

        .email-quick-actions .btn:hover {
          transform: scale(1.05);
        }

        .email-preview {
          font-style: italic;
          border-left: 3px solid var(--timeline-info);
          padding-left: 0.75rem;
          margin-top: 0.5rem;
        }

        .quick-reply-form {
          border: 1px solid var(--timeline-border);
          border-radius: 8px !important;
          background: white !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .activity-footer {
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--timeline-border);
        }

        .user-avatar .avatar-circle {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modern-pagination {
          margin-top: 2rem;
        }

        .modern-pagination-controls .page-link {
          border-radius: 8px !important;
          margin: 0 2px;
          border: 1px solid var(--timeline-border);
          color: var(--timeline-secondary);
          transition: all 0.2s ease;
        }

        .modern-pagination-controls .page-link:hover {
          background: var(--timeline-primary);
          border-color: var(--timeline-primary);
          color: white;
          transform: translateY(-1px);
        }

        .modern-pagination-controls .page-item.active .page-link {
          background: var(--timeline-primary);
          border-color: var(--timeline-primary);
        }

        .clickable-card {
          cursor: pointer;
        }

        .clickable-card:hover {
          transform: translateY(-2px);
        }

        /* Animation for activity cards */
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .activity-card {
          animation: slideInLeft 0.4s ease-out;
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .activity-icon {
            width: 32px;
            height: 32px;
          }

          .email-quick-actions {
            flex-direction: column;
            gap: 0.25rem;
          }

          .activity-header h6 {
            font-size: 1rem;
          }

          .modern-pagination-controls {
            flex-wrap: wrap;
            justify-content: center;
          }
        }
        `
      }} />
    </div>
  );
};

export default DealDetailsTimeline;